import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export default function Auth() {
  const { tr } = useLang();
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "signup" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || password.length < 8) {
      toast.error(tr("formError"));
      return;
    }
    setSubmitting(true);
    try {
      const user = mode === "signup" ? await signup(email, password) : await login(email, password);
      if (!user.onboarded) navigate("/onboarding");
      else navigate("/home");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Map known backend English error messages to localized copy so
      // Spanish learners never see raw English strings for common cases.
      // Any unmapped detail falls through to the raw text (still preferable
      // to a bland "something went wrong"), and if the request never
      // reached the server we fall back to the generic error.
      const localizedByDetail = {
        "Email already registered": tr("authEmailTaken"),
        "Invalid email or password": tr("authInvalidCredentials"),
      };
      toast.error(localizedByDetail[detail] || detail || tr("errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell hideNav>
      <section className="px-5 pt-6 pb-10 space-y-5" data-testid="auth-screen">
        <h1 className="text-2xl font-bold" data-testid="auth-title">
          {mode === "signup" ? tr("createAccount") : tr("signIn")}
        </h1>
        <form onSubmit={onSubmit} className="wpe-card space-y-4" data-testid="auth-form">
          <label className="block">
            <span className="text-[12px] font-semibold text-[rgb(var(--wpe-slate))] block mb-1">
              {tr("email")}
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              className="wpe-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="auth-email-input"
            />
          </label>
          <label className="block">
            <span className="text-[12px] font-semibold text-[rgb(var(--wpe-slate))] block mb-1">
              {tr("password")}
            </span>
            <input
              type="password"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              required
              minLength={mode === "signup" ? 8 : undefined}
              className="wpe-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="auth-password-input"
            />
            {mode === "signup" && (
              <span className="text-[11px] text-[rgb(var(--wpe-muted))] block mt-1">
                {tr("passwordHint")}
              </span>
            )}
          </label>
          <button
            type="submit"
            className="wpe-cta w-full"
            disabled={submitting}
            data-testid="auth-submit-btn"
          >
            {submitting ? tr("processing") : mode === "signup" ? tr("signUp") : tr("signIn")}
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMode((m) => (m === "signup" ? "signin" : "signup"))}
          className="wpe-ghost w-full text-[13px]"
          data-testid="auth-mode-toggle"
        >
          {mode === "signup" ? tr("haveAccount") : tr("noAccount")}
        </button>

        {mode === "signin" && (
          <Link
            to="/forgot-password"
            className="wpe-ghost w-full text-[13px] justify-center"
            data-testid="auth-forgot-password-link"
          >
            {tr("forgotPassword")}
          </Link>
        )}

        <PilotDisclaimer />
      </section>
    </AppShell>
  );
}
